import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Dumbbell, BarChart2, Apple, CreditCard, Star, Bell, ChevronRight, MapPin, Home, LogOut, ScanFace, Camera, PauseCircle } from "lucide-react";

const W = 426, H = 923;
const glow = { filter: "drop-shadow(0 0 8px rgba(244,63,94,.6))" };

const useFitScale = () => {
  const calc = () => Math.min(window.innerWidth / W, window.innerHeight / H);
  const [s, setS] = useState(calc);
  useEffect(() => {
    const on = () => setS(calc());
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);
  return s;
};

export const PortalLogo = () => (
  <div className="flex items-center gap-1.5" data-testid="portal-logo">
    <img src="/icons/icon-192.png" alt="" className="w-[64px] h-[40px] object-cover"/>
    <div className="leading-none">
      <div className="font-portal font-extrabold text-[31px] leading-[28px] tracking-tight text-white">American<span className="text-rose-500">Fit</span></div>
      <div className="text-[6.5px] tracking-[.32em] text-white/90 font-bold mt-1 whitespace-nowrap">ACADEMIA PREMIUM PAGANDO POUCO</div>
    </div>
  </div>
);

export const PortalHeader = ({ children }) => {
  const now = new Date();
  return (
    <div className="flex items-center justify-between gap-2 h-[44px]" data-testid="portal-header">
      <PortalLogo/>
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1 empty:hidden">{children}</div>
        <div className="text-right">
          <div className="text-[15px] text-white leading-tight" data-testid="portal-header-date">{now.toLocaleDateString("pt-BR")}</div>
          <div className="text-[11px] text-slate-400 capitalize">{now.toLocaleDateString("pt-BR", { weekday: "long" })}</div>
        </div>
      </div>
    </div>
  );
};

const IdCard = ({ student, onFace }) => {
  const ok = !student.blocked && !student.suspended;
  const label = student.suspended ? "SUSPENSO" : student.blocked ? "INATIVO" : "ATIVO";
  const long = (student.name || "").length > 30;
  return (
    <div className="h-[211px] rounded-[20px] p-[14px] flex gap-[12px] bg-gradient-to-br from-rose-600 via-rose-700 to-rose-900 shadow-[0_10px_30px_-12px_rgba(225,29,72,.7)]" data-testid="student-portal-digital-id-card">
      <div className="w-[128px] shrink-0 flex flex-col items-center">
        <div className="relative w-[128px] h-[128px]">
          {student.face_photo
            ? <img src={student.face_photo} alt="" className="w-full h-full rounded-full object-cover border-[3px] border-rose-300/70" data-testid="portal-face-photo"/>
            : <div className="w-full h-full rounded-full bg-white/20 border-[3px] border-rose-300/70 flex items-center justify-center font-portal font-black text-[44px]">{student.name[0]}</div>}
          <button onClick={onFace} data-testid="portal-face-update-button" title="Atualizar minha facial" className="absolute right-[-6px] bottom-[-2px] w-[42px] h-[42px] rounded-full bg-white text-rose-600 flex items-center justify-center shadow-lg active:scale-95 transition-transform">
            <Camera className="w-[20px] h-[20px]" strokeWidth={2.4}/>
          </button>
        </div>
        <span data-testid="portal-status-badge" className={`mt-[10px] inline-flex items-center gap-[7px] h-[25px] px-[10px] rounded-full text-[12px] font-black border-2 ${ok ? "bg-emerald-950 border-emerald-500 text-emerald-400" : "bg-black/50 border-amber-400 text-amber-300"}`}>
          <span className={`w-[8px] h-[8px] rounded-full ${ok ? "bg-emerald-400" : "bg-amber-300"}`}/>{label}
        </span>
      </div>
      <div className="min-w-0 flex-1 flex flex-col pt-[18px]">
        <div className="text-[14px] text-white/90 leading-none">Olá,</div>
        <div className={`font-portal font-black uppercase ${long ? "text-[23px] leading-[25px]" : "text-[27px] leading-[29px]"} mt-[8px] line-clamp-2`} data-testid="portal-student-name">{student.name}</div>
        <div className="text-[13px] text-white/90 mt-[4px]">ID: {(student.cpf || "").replace(/\D/g, "") || student.id.slice(0, 8)}</div>
        <div className="mt-auto">
          {student.suspended ? (
            <div className="h-[61px] rounded-[14px] bg-black/25 border border-white/15 px-3 flex flex-col justify-center text-[11px]" data-testid="student-portal-suspended-banner">
              <div className="flex items-center gap-1 font-bold text-amber-300"><PauseCircle className="w-4 h-4"/>Contrato suspenso</div>
              <div>{student.suspended_days_left || 0} dia(s) guardados — procure a recepção.</div>
            </div>
          ) : student.blocked ? (
            <div className="h-[61px] rounded-[14px] bg-black/25 border border-white/15 px-3 flex flex-col justify-center text-[11px]" data-testid="student-portal-blocked-banner">
              <div className="font-bold uppercase">Acesso negado</div>
              <div className="line-clamp-2">{student.block_reason} — regularize com a recepção.</div>
            </div>
          ) : (
            <div className="h-[61px] rounded-[14px] bg-white/10 border border-white/15 px-[12px] flex items-center gap-[12px] text-[12px] leading-[16px]">
              <ScanFace className="w-[36px] h-[36px] shrink-0" strokeWidth={1.8}/>Passe na catraca de qualquer unidade com seu rosto
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const UnitCard = ({ unitId }) => {
  const [units, setUnits] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => { api.get("/portal/units").then(({ data }) => setUnits(data || [])).catch(() => {}); }, []);
  const mine = units.find((u) => u.id === unitId);
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="portal-unit-card" className="w-full h-[70px] rounded-[14px] bg-slate-900/90 border border-slate-700/70 px-[16px] flex items-center gap-[16px] text-left active:border-rose-600/60">
        <MapPin className="w-[30px] h-[30px] text-rose-500 fill-rose-500 shrink-0 [&>circle]:fill-slate-950" style={glow}/>
        <div className="min-w-0 flex-1 leading-tight">
          <div className="text-[12px] text-rose-500">Sua unidade atual</div>
          <div className="font-portal font-extrabold uppercase text-[22px] leading-[24px] text-white truncate">{mine?.name || "—"}</div>
          <div className="text-[12px] text-slate-400 uppercase truncate">{mine?.address || mine?.city || ""}</div>
        </div>
        <ChevronRight className="w-[22px] h-[22px] text-white shrink-0"/>
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-md" data-testid="portal-units-dialog">
          <DialogHeader><DialogTitle>Nossas unidades</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-400 -mt-2">Seu acesso facial vale em qualquer unidade.</p>
          <div className="space-y-2">
            {units.map((u) => (
              <div key={u.id} className={`rounded-xl border p-3 flex items-center gap-3 ${u.id === unitId ? "border-rose-600 bg-rose-600/10" : "border-slate-800"}`} data-testid={`portal-unit-${u.id}`}>
                <MapPin className="w-5 h-5 text-rose-500 shrink-0"/>
                <div className="min-w-0">
                  <div className="font-bold uppercase text-sm">{u.name}{u.id === unitId && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-rose-600">SUA UNIDADE</span>}</div>
                  {(u.address || u.city) && <div className="text-xs text-slate-400">{[u.address, u.city].filter(Boolean).join(" · ")}</div>}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

const TILES = [
  { tab: "workout", icon: Dumbbell, title: "Meu Treino", desc: ["Acesse seus treinos", "personalizados"], rot: "rotate-45" },
  { tab: "progress", icon: BarChart2, title: "Evolução", desc: ["Acompanhe seu", "progresso"] },
  { tab: "diet", icon: Apple, title: "Dieta IA", desc: ["Planos alimentares", "personalizados"] },
  { tab: "plan", icon: CreditCard, title: "Meu Plano", desc: ["Ver detalhes", "do seu plano"] },
  { tab: "rate", icon: Star, title: "Avaliar", desc: ["Avalie sua experiência", "na American Fit"] },
  { tab: "notices", icon: Bell, title: "Avisos", desc: ["Fique por dentro", "das novidades"] },
];

const MenuGrid = ({ onOpen, noticesCount }) => (
  <div className="grid grid-cols-2 gap-x-[10px] gap-y-[9px]" data-testid="portal-menu-grid">
    {TILES.map(({ tab, icon: Icon, title, desc, rot }) => (
      <button key={tab} onClick={() => onOpen(tab)} data-testid={`portal-tile-${tab}`}
        className="relative h-[150px] rounded-[14px] bg-slate-900/90 border border-slate-700/70 flex flex-col items-center pt-[24px] text-center active:border-rose-600/60 active:scale-[.98] transition-transform">
        <Icon className={`w-[52px] h-[52px] text-rose-500 shrink-0 ${rot || ""}`} strokeWidth={2.4} style={glow}/>
        {tab === "notices" && noticesCount > 0 && <span className="absolute top-[10px] right-[10px] min-w-[20px] h-[20px] px-1 rounded-full bg-rose-600 text-white text-[11px] font-bold flex items-center justify-center" data-testid="portal-notices-badge">{noticesCount}</span>}
        <div className="font-portal font-bold text-white text-[22px] mt-[8px] leading-none">{title}</div>
        <div className="text-[12px] leading-[16px] text-slate-400 mt-[7px]">{desc[0]}<br/>{desc[1]}</div>
        <ChevronRight className="w-[18px] h-[18px] text-white absolute right-[16px] top-[112px]" strokeWidth={2.6}/>
      </button>
    ))}
  </div>
);

export const PortalBottomNav = ({ onHome, onLogout, home, inline }) => (
  <div className={`${inline ? "" : "fixed bottom-0 inset-x-0 z-40 pb-[max(.6rem,env(safe-area-inset-bottom))]"} bg-slate-950/95 border-t border-slate-800 pt-[8px]`} data-testid="portal-bottom-nav">
    <div className={`${inline ? "" : "max-w-md mx-auto"} px-[14px] grid grid-cols-2 gap-[10px]`}>
      <button onClick={onHome} data-testid="portal-nav-home" className={`h-[70px] rounded-[14px] flex flex-col items-center justify-center gap-1 text-[17px] font-portal font-bold ${home ? "bg-gradient-to-b from-rose-500 to-rose-700 text-white" : "bg-slate-900 border border-slate-700/70 text-white"}`}>
        <Home className="w-[24px] h-[24px] fill-current"/>Início
      </button>
      <button onClick={onLogout} data-testid="portal-nav-logout" className="h-[70px] rounded-[14px] bg-slate-900 border border-slate-700/70 text-white flex flex-col items-center justify-center gap-1 text-[17px] font-portal font-semibold">
        <LogOut className="w-[24px] h-[24px]"/>Sair
      </button>
    </div>
  </div>
);

// Tela inicial em "prancheta" fixa 426×923 escalada para caber exatamente na tela (mesmas proporções do layout aprovado)
export const PortalHomeScreen = ({ student, onFace, onOpen, noticesCount, onLogout, headerExtras }) => {
  const s = useFitScale();
  return (
    <div className="fixed inset-0 overflow-hidden bg-slate-950" data-testid="portal-home-screen">
      <div className="absolute top-0 left-1/2 origin-top text-white bg-[radial-gradient(ellipse_at_top_left,rgba(159,18,57,.35),transparent_40%),radial-gradient(ellipse_at_bottom_right,rgba(159,18,57,.3),transparent_40%)]"
        style={{ width: W, height: H, transform: `translateX(-50%) scale(${s})` }}>
        <div className="px-[12px] pt-[12px]">
          <PortalHeader>{headerExtras}</PortalHeader>
          <div className="mt-[4px]"><IdCard student={student} onFace={onFace}/></div>
          <div className="mt-[10px]"><UnitCard unitId={student.unit_id}/></div>
          <div className="mt-[10px]"><MenuGrid onOpen={onOpen} noticesCount={noticesCount}/></div>
        </div>
        <div className="absolute bottom-0 inset-x-0 pb-[12px]"><PortalBottomNav inline home onHome={() => {}} onLogout={onLogout}/></div>
      </div>
    </div>
  );
};
